import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Security,
  Notifications,
  Save,
  Refresh,
  ChevronLeft
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { quikqAuthService } from '../services/quikqAuth';

interface TokenDuration {
  value: number; // in minutes
  label: string;
}

const TOKEN_DURATIONS: TokenDuration[] = [
  { value: 15, label: '15 Minutes' },
  { value: 30, label: '30 Minutes' },
  { value: 60, label: '1 Hour' },
  { value: 360, label: '6 Hours' },
  { value: 720, label: '12 Hours' }
];

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Settings state
  const [stayLoggedInDuration, setStayLoggedInDuration] = useState<number>(60); // Default 1 hour
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = () => {
    setLoading(true);
    try {
      // Load settings from localStorage
      const savedDuration = localStorage.getItem('stayLoggedInDuration');
      const savedNotifications = localStorage.getItem('notificationsEnabled');
      const savedAutoRefresh = localStorage.getItem('autoRefreshEnabled');

      if (savedDuration) {
        setStayLoggedInDuration(parseInt(savedDuration));
      }
      if (savedNotifications !== null) {
        setNotificationsEnabled(savedNotifications === 'true');
      }
      if (savedAutoRefresh !== null) {
        setAutoRefreshEnabled(savedAutoRefresh === 'true');
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Save settings to localStorage
      localStorage.setItem('stayLoggedInDuration', stayLoggedInDuration.toString());
      localStorage.setItem('notificationsEnabled', notificationsEnabled.toString());
      localStorage.setItem('autoRefreshEnabled', autoRefreshEnabled.toString());

      // Update auth service with new token duration
      quikqAuthService.setTokenDuration(stayLoggedInDuration);

      setSuccess('Settings saved successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess('');
      }, 3000);

    } catch (error) {
      console.error('Error saving settings:', error);
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleResetSettings = () => {
    setStayLoggedInDuration(60); // Default 1 hour
    setNotificationsEnabled(true);
    setAutoRefreshEnabled(true);
    setError('');
    setSuccess('');
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ 
      p: { xs: 2, sm: 3, md: 4 }, 
      pb: { xs: 12, sm: 12, md: 12 }, 
      bgcolor: '#f5f6fa', 
      minHeight: '100vh',
      position: 'relative',
      zIndex: 0,
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '300px',
        height: '300px',
        backgroundImage: 'url(/NSFIcon.png)',
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        opacity: 0.03,
        zIndex: 0,
        pointerEvents: 'none'
      },
      '& > *': {
        position: 'relative',
        zIndex: 1
      }
    }}>
      {/* Header with Split Watermark - Left: NSFLogo, Right: Empty (for future custom logo) */}
      <Box sx={{ 
        position: 'relative', 
        mb: 3,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -50,
          left: -50,
          width: '100%',
          height: '200px',
          background: `url('/NSFIcon.png')`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          opacity: 0.08,
          zIndex: 0,
          pointerEvents: 'none'
        }
      }}>
        <Container maxWidth="md">
          <Box sx={{ mt: { xs: 2, sm: 3, md: 4 }, position: 'relative', zIndex: 1 }}>
            {/* Back Navigation */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 3,
              cursor: 'pointer',
              '&:hover': { opacity: 0.7 }
            }}
            onClick={() => navigate('/profile')}
            >
              <ChevronLeft sx={{ fontSize: 20, mr: 0.5 }} />
              <Typography variant="body2" fontWeight="500">Back</Typography>
            </Box>

            {/* Header */}
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <SettingsIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                Settings
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Manage your preferences and application settings
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>

        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {/* Authentication Settings */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Security sx={{ mr: 2, color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Authentication
              </Typography>
            </Box>
            
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Stay Logged In</InputLabel>
              <Select
                value={stayLoggedInDuration}
                label="Stay Logged In"
                onChange={(e) => setStayLoggedInDuration(e.target.value as number)}
              >
                {TOKEN_DURATIONS.map((duration) => (
                  <MenuItem key={duration.value} value={duration.value}>
                    {duration.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose how long you want to stay logged in before being automatically signed out.
            </Typography>
          </CardContent>
        </Card>

        {/* Notification Settings */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Notifications sx={{ mr: 2, color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Notifications
              </Typography>
            </Box>

            <List>
              <ListItem>
                <ListItemText
                  primary="Enable Notifications"
                  secondary="Receive alerts and updates about your account"
                />
                <ListItemSecondaryAction>
                  <Switch
                    checked={notificationsEnabled}
                    onChange={(e) => setNotificationsEnabled(e.target.checked)}
                    color="primary"
                  />
                </ListItemSecondaryAction>
              </ListItem>
            </List>
          </CardContent>
        </Card>

        {/* Application Settings */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <SettingsIcon sx={{ mr: 2, color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Application
              </Typography>
            </Box>

            <List>
              <ListItem>
                <ListItemText
                  primary="Auto Refresh Data"
                  secondary="Automatically refresh data when returning to the app"
                />
                <ListItemSecondaryAction>
                  <Switch
                    checked={autoRefreshEnabled}
                    onChange={(e) => setAutoRefreshEnabled(e.target.checked)}
                    color="primary"
                  />
                </ListItemSecondaryAction>
              </ListItem>
            </List>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleResetSettings}
            disabled={saving}
          >
            Reset to Default
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSaveSettings}
            disabled={saving}
            sx={{ minWidth: 120 }}
          >
            {saving ? <CircularProgress size={20} /> : 'Save Settings'}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default Settings;
